import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController, Platform } from '@ionic/angular/lazy';
import { OrderService } from 'src/app/services/order.service';
import moment from 'moment-timezone';
import { DateTimePickerPage } from 'src/app/modals/date-time-picker/date-time-picker.page';
import { ObserverService } from 'src/app/services/observer.service';
import { Capacitor } from '@capacitor/core';
import { Decimal } from "decimal.js";

@Component({
    selector: 'app-order-return-add',
    templateUrl: './order-return-add.page.html',
    styleUrls: ['./order-return-add.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class OrderReturnAddPage implements OnInit {

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    moment = moment;
    user = null;
    @Input() order_id = null;
    order = null;
    returnable_items = [];
    formatted_date = null;
    date_modal_css_class = '';

    params = {
        items: []
    };

    // modal flags
    date_picker_modal_open = false;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        private loadingController: LoadingController,
        private orderService: OrderService,
        private platform: Platform,
        private observerService: ObserverService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.date_modal_css_class = 'backdrop';
        }

        if(this.order_id) {


            this.params['date'] = this.moment().toISOString(true);
            this.formatted_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");

            this.loadData().then(() => {

                for(let i = 0; i < this.order.items.length; i++) {

                    if(this.order.items[i].returnable_quantity != 0) {

                        let item = {};
                        item['id'] = this.order.items[i].id;
                        item['title'] = this.order.items[i].title;
                        item['returnable_quantity'] = this.order.items[i].returnable_quantity;
                        item['quantity'] = 0;
                        item['quantity_decimal_places'] = this.order.items[i].quantity_decimal_places;

                        if(this.order.items[i].sku) {
                            item['sku'] = this.order.items[i].sku;
                        }

                        if(this.order.items[i].measurement_unit) {
                            item['measurement_unit'] = this.order.items[i].measurement_unit;
                        }

                        this.returnable_items.push(item);
                    }
                }

            })
            .catch(() => { });

        }

        this.observerService.subscribe('network_status_changed|order_return_add_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to add a return, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|order_return_add_modal");
    }

    loadData() {
        let promise = new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading order").then(() => {
                this.loadOrder().then(() => {
                    this.loading_spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
        return promise;
    }

    loadOrder() {
        let promise = new Promise<void>((resolve, reject) => {
            this.order = null;
            this.orderService.getOrder(this.order_id).then((order) => {
                if(order) {
                    this.order = order;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
        return promise;
    }

    adjustItemQuantity(item_id, change_mode){
        for(let i = 0; i < this.returnable_items.length; i++) {
            if(item_id == this.returnable_items[i].id) {
                let step = Math.pow(10, -this.returnable_items[i].quantity_decimal_places);
                if(change_mode === 'add'){
                    if(this.returnable_items[i].quantity < this.returnable_items[i].returnable_quantity){
                        this.returnable_items[i].quantity = Number(new Decimal(this.returnable_items[i].quantity).plus(step));
                    }
                }
                else if(change_mode === 'subtract'){
                    if(this.returnable_items[i].quantity > 0){
                        this.returnable_items[i].quantity = Number(new Decimal(this.returnable_items[i].quantity).minus(step));
                    }
                }
            }
        }
    }

    close() {
        this.modalController.dismiss();
    }

    add() {

        let validated = true;
        let return_has_items = false;

        for(let i = 0; i < this.returnable_items.length; i++) {

            let item = this.returnable_items[i];
            let quantity_invalid_char = item.quantity.toString().search(/[^0-9.]/g);

            if(quantity_invalid_char !== -1) {
                this.presentAlert('Invalid quantity', `Item "${item.title}" has an invalid quantity.`);
                validated = false;
                break;
            }
            else if(item.quantity > 0) {
                return_has_items = true;
                if(item.quantity > item.returnable_quantity){
                    this.presentAlert('Over returned', `You have returned an item "${item.title}". The max quantity is ${item.returnable_quantity}.`);
                    validated = false;
                    break;
                }
            }
            else if(item.quantity < 0) {
                this.presentAlert('Negative quantity', `Item "${item.title}" has a negative quantity. Please make the quantity greater or equal to zero.`);
                validated = false;
                break;
            }

        }

        if(!return_has_items) {
            this.presentAlert('Empty return', 'You need at least one item.');
            validated = false;
        }

        if (this.params['date'] == null || this.params['date'].trim() === "") {
            validated = false;
            this.presentAlert('Missing date', 'Please select a date.');
        }

        if(validated) {

            let items = [];
            
            for(let i = 0; i < this.returnable_items.length; i++) {
                if(this.returnable_items[i].quantity > 0) {
                    items.push({
                        id: this.returnable_items[i].id,
                        quantity: this.returnable_items[i].quantity
                    });
                }
            }

            this.params.items = items;

            this.params['date'] = this.moment(this.params['date']).utc().toISOString();

            this.addOrderReturn().then(() => {
                this.modalController.dismiss();
            });

        }
    }

    addOrderReturn() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Adding return").then(() => {
                this.orderService.addOrderReturn(this.order.id, this.params).then(() => {
                    this.spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.spinner.dismiss();
                    reject();
                });
            });
        })
    }

    async presentDateTimePickerModal() {
        if(!this.date_picker_modal_open) {
            this.date_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: DateTimePickerPage,
                cssClass: this.date_modal_css_class,
                componentProps: {
                    date: this.params['date'],
                    format: "date-time",
                    clear_date: false
                }
            })
            modal.onDidDismiss().then((data) => {
                this.date_picker_modal_open = false;
                if(data.data) {
                    this.params['date'] = data.data['date'];
                    this.formatted_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async editItemQuantity(item) {

        let header = "";

        if(item["measurement_unit"] != undefined && item["measurement_unit"] != null) {
            header = "Quantity" + " " + "(" + item["measurement_unit"].symbol + ")";
        }
        else {
            header = "Quantity";
        }

        const alert = await this.alertController.create({
            header: header,
            cssClass:'alert',
            inputs: [{
                name: 'quantity',
                type: 'number',
                value: item.quantity,
                min: 0
            }],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                    cssClass: 'secondary',
                    handler: () => {
                    }
                }, 
                {
                    text: 'Ok',
                    handler: (data) => {
                        for(let i = 0; i < this.returnable_items.length; i++) {
                            if(item.id == this.returnable_items[i].id) {

                                let returnable_item = this.returnable_items[i];

                                // if dp=0, only accept numbers (allows negative)
                                if(returnable_item.quantity_decimal_places == 0 && /[^0-9-]/.test(data.quantity)) {
                                    returnable_item.quantity = item.quantity;
                                }
                                // if dp > 0, only accept numbers/decimal points (allows negative)
                                else if(returnable_item.quantity_decimal_places > 0 && /[^0-9.-]/.test(data.quantity)) {
                                    returnable_item.quantity = item.quantity;
                                }
                                else if(returnable_item.quantity_decimal_places > 0 && data.quantity.startsWith('.')) {
                                    returnable_item.quantity = item.quantity;
                                }
                                else if(returnable_item.quantity_decimal_places > 0 && data.quantity.endsWith('.')) {
                                    returnable_item.quantity = item.quantity;
                                }
                                // if dp > 0, ensure decimal places not exceeded
                                else if (returnable_item.quantity_decimal_places > 0 && data.quantity.includes('.') && data.quantity.split('.')[1].length > returnable_item.quantity_decimal_places) {
                                    returnable_item.quantity = item.quantity;
                                }
                                else if(!data.quantity.length) {
                                    returnable_item.quantity = item.quantity;
                                }
                                else if(data.quantity < 0) {
                                    returnable_item.quantity = 0;
                                }
                                else if (data.quantity > returnable_item.returnable_quantity) {
                                    returnable_item.quantity = returnable_item.returnable_quantity;
                                }
                                else {
                                    returnable_item.quantity = Number(new Decimal(data.quantity).toDecimalPlaces(returnable_item.quantity_decimal_places));
                                }

                                break;
                            }
                        }

                    }
                }
            ]
        });
        await alert.present();
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}